import { useGetPatientById } from "@elation/api-client/src/stitch-web";
import type { Patient } from "@elation/api-client/src/stitch-web/models";
import storage from "@src/utils/storage";
import { getLocalTimezone } from "@src/utils/timezone";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export interface AppStore {
  theme: "dark" | "light" | undefined;
  setTheme: (theme: "dark" | "light") => void;
  practiceTimezone: string | undefined;
  userTimezone: string;
  setPracticeTimezone: (practiceTimezone: string | undefined) => void;
  patient_id: number | null;
  setPatientId: (patient_id: number | null) => void;
}

export const appStore = create<AppStore>()(
  persist(
    (set) => ({
      patient_id: null,
      setPatientId: (patient_id) => {
        set((state) => {
          return {
            ...state,
            patient_id,
          };
        });
      },
      theme: undefined,
      setTheme: (theme) => {
        set((state) => {
          return {
            ...state,
            theme,
          };
        });
      },

      practiceTimezone: undefined,
      setPracticeTimezone: (practiceTimezone) => {
        set((state) => {
          return {
            ...state,
            practiceTimezone,
          };
        });
      },
      userTimezone: getLocalTimezone(),
    }),
    {
      name: "app-store",
      storage: createJSONStorage(() => storage),
    },
  ),
);

export function useLocalAppThemePreference() {
  const id = useAppStore((state) => state.theme);

  return id;
}

/**
 * Returns the timezone used for local date display and serialization.
 */
export function useEffectiveTimezone(): string {
  const practiceTimezone = useAppStore((state) => state.practiceTimezone);
  const userTimezone = useAppStore((state) => state.userTimezone);
  return practiceTimezone || userTimezone;
}

export function usePatientId(): [
  patient_id: number | null,
  setPatientId: (patient_id: number | null) => void,
] {
  const patient_id = useAppStore((state) => state.patient_id);
  const setPatientId = useAppStore((state) => state.setPatientId);
  return [patient_id, setPatientId];
}

export function useCurrentPatient(): Patient | null {
  const patient_id = useAppStore((state) => state.patient_id);

  const { data: patientData } = useGetPatientById(Number(patient_id), {
    query: {
      enabled: !!patient_id,
    },
  });

  return patientData?.data ?? null;
}

export const useAppStore = appStore;
